import { UnifiedButton } from '@/components/ui/unified-button'
import { format } from 'date-fns'
import { Calendar, Search } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useUserSettings } from '../api/hooks/useSettings'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { EmptyState } from '../components/ui/EmptyState'
import { Input } from '../components/ui/input'
import { WorkoutsPageSkeleton } from '../components/ui/loading'
import { LoadingSpinner } from '../components/ui/loading-spinner'
import WorkoutCalendar from '../components/WorkoutCalendar'
import WorkoutCard from '../components/WorkoutCard'
import WorkoutDetailModal from '../components/WorkoutDetailModal'
import { searchWorkouts } from '../lib/searchParser'
import { useInfiniteWorkoutData } from '../api/hooks/useWorkoutData'
import type { Workout } from '../types'
import { useAntiSpamDebounce } from '../utils/rate-limiting'

export default function WorkoutsPage() {
  // Get user settings for workout tags
  const { data: userSettings } = useUserSettings()

  // Workout page state
  const [selectedDateRange, setSelectedDateRange] = React.useState<{
    start: Date
    end: Date
  } | null>(null)
  const [localSearchQuery, setLocalSearchQuery] = React.useState('')

  // Invisible anti-spam debouncing - only delays machine-like rapid typing
  const workoutSearchQuery = useAntiSpamDebounce(localSearchQuery, 200)

  const [showCalendar, setShowCalendar] = React.useState(false)
  const [selectedWorkout, setSelectedWorkout] = React.useState<Workout | null>(null)
  const calendarRef = React.useRef<HTMLDivElement>(null)

  // Close calendar when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false)
      }
    }

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCalendar])

  // Handle escape key for calendar
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCalendar(false)
      }
    }

    if (showCalendar) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showCalendar])

  // Use infinite query from centralized provider
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } =
    useInfiniteWorkoutData({
      pageSize: 9
    })

  // Intersection observer for infinite scroll
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0
  })

  // Load more when scrolling to bottom
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Flatten all pages of workouts and apply client-side filters
  const filteredWorkouts = useMemo(() => {
    const allWorkouts = data?.pages.flatMap((page) => page.workouts) || []
    // Move workoutTags computation inside useMemo to prevent stale closure
    const workoutTags = userSettings?.workoutTags || {}

    // Apply date range filter
    let filtered = allWorkouts
    if (selectedDateRange) {
      filtered = filtered.filter((workout) => {
        const workoutDate = new Date(workout.start_time)
        return workoutDate >= selectedDateRange.start && workoutDate <= selectedDateRange.end
      })
    }

    // Apply search filter using fuzzy search
    if (workoutSearchQuery) {
      filtered = searchWorkouts(workoutSearchQuery, filtered, workoutTags)
    }

    return filtered
  }, [data, selectedDateRange, workoutSearchQuery, userSettings?.workoutTags])

  // Clear filters
  const clearFilters = useCallback(() => {
    setSelectedDateRange(null)
    setLocalSearchQuery('')
  }, [])

  const hasActiveFilters = selectedDateRange || workoutSearchQuery

  if (isLoading) {
    return <WorkoutsPageSkeleton />
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-screen-lg p-4 md:p-6">
        <EmptyState
          icon={Search}
          title="Error loading workouts"
          description={error || 'Failed to load workout data'}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-lg p-4 md:p-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Workouts</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Browse and track all your workout sessions
          </p>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          {/* Search Bar */}
          <div className="flex gap-1">
            <div className="relative flex-1" ref={calendarRef}>
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
              <Input
                placeholder="Search workouts (e.g., 'upper april bench press', 'last week squat', 'pr day', 'intense')"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault() // Prevent form submission
                  }
                }}
                className="pr-10 pl-10 max-md:h-11"
              />

              {/* Calendar button inside input */}
              <Button
                onClick={() => setShowCalendar(!showCalendar)}
                variant="ghost"
                size="sm"
                className="hover:bg-muted/50 absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2 transform cursor-pointer p-0"
                aria-haspopup="dialog"
                aria-expanded={showCalendar}
                aria-label={
                  selectedDateRange
                    ? `Select dates. Currently: ${format(
                        selectedDateRange.start,
                        'MMM d'
                      )} - ${format(selectedDateRange.end, 'MMM d')}`
                    : 'Select dates'
                }
              >
                <Calendar
                  className={`h-4 w-4 ${selectedDateRange ? 'text-primary' : 'text-muted-foreground'}`}
                />
              </Button>

              {showCalendar && (
                <div
                  className="bg-popover animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 absolute top-full right-0 z-50 mt-1 rounded-md border p-4 shadow-md"
                  role="dialog"
                  aria-label="Date range picker"
                >
                  <WorkoutCalendar
                    selectedDateRange={selectedDateRange}
                    onDateRangeChange={setSelectedDateRange}
                    onClose={() => setShowCalendar(false)}
                  />
                </div>
              )}
            </div>
            <UnifiedButton
              onClick={() => {
                /* Search is now automatic via debounced query */
              }}
              variant="secondary"
              className="h-9"
              disabled
            >
              Search
            </UnifiedButton>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Active filters:</span>
              {selectedDateRange && (
                <Badge variant="secondary">
                  {new Date(selectedDateRange.start).toLocaleDateString()} -{' '}
                  {new Date(selectedDateRange.end).toLocaleDateString()}
                </Badge>
              )}
              {workoutSearchQuery && (
                <Badge variant="secondary">&quot;{workoutSearchQuery}&quot;</Badge>
              )}
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-auto py-1">
                Clear all
              </Button>
            </div>
          )}
        </div>

        {/* Workout List */}
        {filteredWorkouts.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <EmptyState
              icon={Search}
              title="No workouts found"
              description={
                hasActiveFilters
                  ? hasNextPage
                    ? 'No matches in loaded workouts. Scroll to load more or adjust filters'
                    : 'Try adjusting your filters'
                  : 'Start logging your workouts to see them here'
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {filteredWorkouts.map((workout) => (
              <WorkoutCard
                key={workout.id}
                workout={workout}
                onClick={() => setSelectedWorkout(workout)}
              />
            ))}
          </div>
        )}

        {/* Load More Indicator */}
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isFetchingNextPage && <LoadingSpinner className="text-muted-foreground" />}
          {!hasNextPage && filteredWorkouts.length > 0 && (
            <p className="text-muted-foreground text-sm">No more workouts to load</p>
          )}
        </div>
      </div>

      {/* Workout Detail Modal */}
      {selectedWorkout && (
        <WorkoutDetailModal
          workout={selectedWorkout}
          isOpen={!!selectedWorkout}
          onClose={() => setSelectedWorkout(null)}
        />
      )}
    </div>
  )
}
